import { useEffect, useRef, useState } from "react";

export type PlayerState = "idle" | "connecting" | "playing" | "error" | "stopped";

interface Options {
  audio?: boolean;
}

// Tiempo maximo entre conectar el WS y ver el primer frame en pantalla.
// Sin esto, una falla silenciosa (DVR caido, stream sin frames) deja al
// usuario mirando "Conectando..." indefinidamente. 35s deja margen al
// timeout RTSP de go2rtc (30s, via #timeout=30 en la URL) — si la fuente
// va a fallar por i/o timeout, manda su error JSON antes que dispare este.
const CONNECT_TIMEOUT_MS = 35000;

// Patrones que clasifican errores de go2rtc como "transitorios" — vale la
// pena reintentar 1 vez. Lo demas (connection refused, no route, 401, etc.)
// es fatal y falla inmediato sin esperar.
const TRANSIENT_ERROR_RE = /timeout|EOF|reset/i;
const RETRY_DELAY_MS = 1500;

// Codecs aceptados al pedir MSE a go2rtc. Usamos la nomenclatura corta
// (H264/H265/AAC/OPUS) que go2rtc trata como "cualquier profile/level" — esto
// es necesario porque los canales Dahua negocian profiles HEVC distintos
// segun resolucion (L93 para 720p, L120/L123 para 1080p, L150/L153 para 4K) y
// listar el formato MIME exacto por adelantado falla si el canal usa otro.
// El listado mixto avc1.*,hvc1.* causa "codecs not matched" en go2rtc 1.9.x,
// asi que pedimos los tipos en formato corto.
const VIDEO_CODECS = "H264,H265";
const AUDIO_CODECS = "AAC,OPUS";

// Preflight HEVC support: si el browser NO puede decodificar ningun perfil HEVC
// (Firefox, Chrome sin licencia de codec, builds Linux sin libavcodec h265),
// pedirle a go2rtc solo H264 evita que devuelva fragmented MP4 HEVC indecodable.
// Cuando la fuente sea HEVC nativo, go2rtc respondera con "codecs not matched"
// y ese error se traduce al sentinel __CODEC_UNSUPPORTED__ que dispara el
// fallback transcode (ver mas abajo).
// Chequeamos L120 (1080p) y L153 (4K) en hev1/hvc1: cubre los 4 sample entries
// que emite go2rtc 1.9.x para canales Dahua.
const SUPPORTS_HEVC =
  typeof MediaSource !== "undefined" && (
    MediaSource.isTypeSupported('video/mp4; codecs="hev1.1.6.L153.B0"') ||
    MediaSource.isTypeSupported('video/mp4; codecs="hvc1.1.6.L153.B0"') ||
    MediaSource.isTypeSupported('video/mp4; codecs="hev1.1.6.L120.B0"') ||
    MediaSource.isTypeSupported('video/mp4; codecs="hvc1.1.6.L120.B0"')
  );

// Sentinel reconocido por WebRTCPlayer.tsx para disparar onDecodeError.
// Cualquier mensaje de error que arranque con este prefix se trata como
// "codec no soportado nativamente, activar transcode H.264".
const CODEC_UNSUPPORTED_SENTINEL = "__CODEC_UNSUPPORTED__";

// Texts que go2rtc 1.9.x emite cuando los codecs pedidos no matchean la fuente
// (p.ej. pedimos solo H264 y la camara sirve HEVC nativo sin transcode).
const CODEC_MISMATCH_RE = /codecs not matched|no media|no producer/i;

// Texts que go2rtc emite cuando el WRAPPER ffmpeg (path transcode) falla a abrir
// el RTSP del NVR. Tipico en VPN lenta + timeout corto. Reconocer esto permite
// al frontend caer al path directo en lugar de quedar atrapado en transcode.
const EXEC_RTSP_FAIL_RE = /exec\/rtsp|Invalid data found|Error opening input/i;

// Sentinel para el caso "el wrapper transcode no pudo abrir el stream".
// Distinto de __CODEC_UNSUPPORTED__ que es "el browser no decodifica este codec".
const TRANSCODE_FAILED_SENTINEL = "__TRANSCODE_FAILED__";

// Firma del error nativo Go que aparece cuando el RTSP del NVR no responde
// al DESCRIBE/SETUP a tiempo. Usamos esto para dar al usuario un mensaje mas
// accionable que el crudo "read tcp ...:554: i/o timeout".
const IO_TIMEOUT_RE = /i\/o timeout|read tcp.*:554/i;

// Codes WebSocket que NO indican cierre normal — el server corto la conexion
// sin enviar un frame close apropiado, lo que tipicamente coincide con que
// go2rtc detecto un error con la fuente RTSP y cerro el WS por su lado sin
// llegar a enviar primero un {type:"error"} al cliente.
//   1005 = No Status Received (sin codigo explicito)
//   1006 = Abnormal Closure (sin frame close)
//   1011 = Server Error (excepcion server-side)
const ABNORMAL_WS_CLOSE_CODES = new Set([1005, 1006, 1011]);

// Hook de reproduccion en vivo. A pesar del nombre `useWebRTC`, internamente
// usa MSE (Media Source Extensions) sobre el WS de go2rtc — eso evita la
// dependencia de UDP/8555 y de NAT traversal, que rompian la vista en vivo
// para clientes detras de VPN o firewalls restrictivos.
export function useWebRTC(
  streamName: string | null,
  opts: Options = { audio: false },
  retryNonce: number = 0,
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const stoppedRef = useRef(false);
  const playingRef = useRef(false);
  const [state, setState] = useState<PlayerState>("idle");
  const [error, setError] = useState<string | null>(null);
  // Bandera de "ya reintentamos automaticamente para este stream" — evita
  // loops si la fuente sigue lenta tras el primer retry. Se resetea cuando
  // cambia streamName o cuando el usuario cliquea Reintentar (retryNonce).
  const retryAttemptedRef = useRef(false);
  // Nonce interno que el effect principal usa como dep — al incrementarlo
  // desde el handler de error, el effect se cleanupea y vuelve a montar
  // creando un WS fresco. Equivalente al retryNonce externo pero disparado
  // automaticamente por el clasificador de errores.
  const [internalRetry, setInternalRetry] = useState(0);

  useEffect(() => {
    retryAttemptedRef.current = false;
    setInternalRetry(0);
  }, [streamName, retryNonce]);

  useEffect(() => {
    if (!streamName) {
      setState("idle");
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    stoppedRef.current = false;
    playingRef.current = false;
    setState("connecting");
    setError(null);

    // Cancellation token LOCAL a este run del effect. Necesario porque cuando
    // cambian las deps (p.ej. opts.audio: false -> true) el cleanup corre, cierra
    // el WS viejo, luego el effect nuevo ejecuta y resetea stoppedRef=false. El
    // evento onclose del WS VIEJO llega en otro tick async y veria stoppedRef
    // ya reseteado, disparando un "Conexion cerrada antes de playback" fantasma.
    // `cancelled` se captura en el closure de los handlers del WS viejo y se
    // marca true en SU cleanup, asi ellos saben que estan obsoletos.
    let cancelled = false;

    // Cola de chunks fMP4. appendBuffer es asincrono: si llega un chunk
    // mientras SourceBuffer.updating es true, lo encolamos y lo servimos
    // desde el handler de "updateend".
    const queue: ArrayBuffer[] = [];
    let connectTimer: ReturnType<typeof setTimeout> | null = null;
    // Timer del retry automatico tras error transitorio. Mientras esta pendiente,
    // el onclose del WS NO debe disparar fail() — la reapertura ya esta programada.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const fail = (msg: string) => {
      if (cancelled || stoppedRef.current) return;
      setError(msg);
      setState("error");
    };

    const flush = () => {
      const sb = sourceBufferRef.current;
      const ms = mediaSourceRef.current;
      if (!sb || sb.updating || !ms || ms.readyState !== "open") return;
      const chunk = queue.shift();
      if (!chunk) return;
      try {
        sb.appendBuffer(chunk);
      } catch (e) {
        // QuotaExceededError tipicamente al acumular >30s. Truncamos el inicio
        // del buffer y reintentamos el chunk perdido para no perder frames.
        if ((e as DOMException).name === "QuotaExceededError" && sb.buffered.length > 0) {
          try {
            const start = sb.buffered.start(0);
            const end = sb.buffered.end(0);
            const cutoff = Math.max(start + 1, end - 5);
            queue.unshift(chunk);
            sb.remove(start, cutoff);
          } catch {
            fail(`SourceBuffer quota: ${(e as Error).message}`);
          }
        } else {
          fail(`SourceBuffer: ${(e as Error).message || String(e)}`);
        }
      }
    };

    // 1. Crear MediaSource y conectarlo al <video>
    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    const objectUrl = URL.createObjectURL(mediaSource);
    objectUrlRef.current = objectUrl;
    video.src = objectUrl;

    // 2. Abrir WS hacia go2rtc (mismo endpoint que WebRTC, distinto protocolo)
    const wsProto = location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProto}//${location.host}/go2rtc/api/ws?src=${encodeURIComponent(streamName)}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    // 3. Timeout para abortar si no llegan frames
    connectTimer = setTimeout(() => {
      if (!stoppedRef.current && !playingRef.current) {
        fail(`Timeout: no llego video en ${CONNECT_TIMEOUT_MS / 1000}s. Verifica que el DVR responde y que go2rtc esta corriendo.`);
        try { ws.close(); } catch {}
      }
    }, CONNECT_TIMEOUT_MS);

    // 4. Pasar a "playing" cuando el <video> realmente empieza a reproducir
    const onPlaying = () => {
      playingRef.current = true;
      if (connectTimer) { clearTimeout(connectTimer); connectTimer = null; }
      if (!stoppedRef.current) setState("playing");
    };
    video.addEventListener("playing", onPlaying);

    ws.onopen = () => {
      // Cuando el browser no decodifica HEVC, pedimos solo H264. Si la camara
      // sirve HEVC nativo, go2rtc respondera con codec-mismatch y el handler
      // de abajo lo traduce al sentinel para activar transcode.
      const baseVideo = SUPPORTS_HEVC ? VIDEO_CODECS : "H264";
      const codecs = opts.audio ? `${baseVideo},${AUDIO_CODECS}` : baseVideo;
      try {
        // Pedimos MSE: go2rtc nos respondera con un mensaje JSON {type:"mse",value:"<codec>"}
        // y luego con chunks binarios fragmented MP4.
        ws.send(JSON.stringify({ type: "mse", value: codecs }));
      } catch (e) {
        fail(`WS send: ${(e as Error).message || String(e)}`);
      }
    };

    ws.onmessage = async (m) => {
      // Mensajes de control vienen como string (JSON)
      if (typeof m.data === "string") {
        let msg: any;
        try { msg = JSON.parse(m.data); } catch { return; }
        if (msg.type === "mse" && msg.value) {
          // go2rtc puede devolver el codec en formato MIME completo
          // (`video/mp4; codecs="hvc1..."`) o solo el codec — soportamos ambos.
          const raw = String(msg.value);
          const baseMime = raw.startsWith("video/") || raw.startsWith("audio/")
            ? raw
            : `video/mp4; codecs="${raw}"`;
          // Bug observado 2026-05-18 con NVR Dahua XVR analógico (device "1231"):
          // go2rtc anuncia `hvc1.1.6.L153.B0` hardcoded para todos los streams HEVC,
          // pero el sample entry interno del fragmented MP4 que emite es `hev1`
          // (parameter sets in-band, no en stsd). Chrome a veces decodifica
          // OK con `hvc1` declarado y bitstream `hev1`, pero canales cuyo SPS tiene
          // `general_profile_compatibility_flags=0x60000000` (Main+Main10 compat)
          // disparan "HTMLMediaElement.error attribute is not null" al appendBuffer.
          // Probar `hev1` primero — matchea el sample entry real y Chrome procesa
          // los parameter sets in-band correctamente. Fallback al original si Chrome
          // no soporta `hev1` (ej. browsers viejos).
          const altMime = baseMime.replace(/"hvc1\./g, '"hev1.');
          const candidates = altMime !== baseMime ? [altMime, baseMime] : [baseMime];
          const mime = candidates.find((m) => MediaSource.isTypeSupported(m));
          if (!mime) {
            console.warn("[player] codec rechazado por isTypeSupported:", baseMime);
            fail(`${CODEC_UNSUPPORTED_SENTINEL}: ${baseMime}. El navegador no decodifica este codec; activando re-codificacion.`);
            return;
          }
          const setup = () => {
            if (stoppedRef.current) return;
            try {
              const sb = mediaSource.addSourceBuffer(mime);
              sb.mode = "segments";
              sb.addEventListener("updateend", flush);
              sourceBufferRef.current = sb;
              flush();
              // Intentar reproducir explicitamente para browsers que no
              // autoplay (algunos chromium con policy=user-required).
              video.play().catch(() => {});
            } catch (e) {
              fail(`SourceBuffer init: ${(e as Error).message || String(e)}`);
            }
          };
          if (mediaSource.readyState === "open") setup();
          else mediaSource.addEventListener("sourceopen", setup, { once: true });
        } else if (msg.type === "error") {
          const val = msg.value ? String(msg.value) : "error";
          // Path transcode caido: el wrapper ffmpeg de go2rtc no pudo abrir el
          // RTSP del NVR (timeout, VPN lenta, bitstream corrupto). Si estamos
          // ya en un stream `_h264`, no tiene sentido reintentar igual — el
          // padre debe limpiar el flag transcode y reintentar el path directo.
          // Va ANTES del codec-mismatch porque algunos errores VPN matchean
          // ambos regex y queremos el desambiguador transcode-falla primero.
          if (EXEC_RTSP_FAIL_RE.test(val) && streamName.endsWith("_h264")) {
            console.warn("[player] transcode falla:", val);
            fail(`${TRANSCODE_FAILED_SENTINEL}: ${val}`);
            return;
          }
          // Codec mismatch: pedimos solo H264 (porque el browser no decodifica
          // HEVC) pero la fuente sirve HEVC nativo. Disparar el sentinel para
          // que WebRTCPlayer active el fallback transcode H.264 via ffmpeg.
          // Va ANTES del clasificador transitorio porque "codecs not matched"
          // no es transitorio y nunca se va a resolver con un retry simple.
          if (CODEC_MISMATCH_RE.test(val)) {
            console.warn("[player] go2rtc no-match:", val);
            fail(`${CODEC_UNSUPPORTED_SENTINEL}: go2rtc no pudo entregar un codec soportado (${val}).`);
            return;
          }
          // Clasificar el error: si es transitorio (timeout/EOF/reset) y no
          // hemos retried aun, cerrar el WS y re-abrir tras un delay corto.
          // Lo demas falla inmediato.
          const isTransient = TRANSIENT_ERROR_RE.test(val);
          if (
            isTransient
            && !retryAttemptedRef.current
            && !playingRef.current
            && !stoppedRef.current
            && !cancelled
          ) {
            retryAttemptedRef.current = true;
            if (connectTimer) { clearTimeout(connectTimer); connectTimer = null; }
            try { ws.close(); } catch {}
            retryTimer = setTimeout(() => {
              if (cancelled || stoppedRef.current) return;
              // Incrementar el nonce interno re-ejecuta este useEffect con
              // un WS fresco. retryAttemptedRef sigue en true para no entrar
              // en loop si el segundo intento tambien falla por timeout.
              setInternalRetry((n) => n + 1);
            }, RETRY_DELAY_MS);
          } else {
            // Mensaje mas accionable cuando el error es "i/o timeout" o
            // "read tcp ...:554" — firma del Go RTSP client. Indica que el
            // NVR acepto el TCP pero no respondio al DESCRIBE/SETUP a tiempo.
            // Causas tipicas: saturacion de slots RTSP en el NVR, NVR ocupado
            // con otra conexion (DSS), o latencia transitoria en VPN.
            if (IO_TIMEOUT_RE.test(val)) {
              fail("El NVR no respondio al pedido RTSP en 30s. Puede estar saturado o lento; reintenta en unos segundos.");
            } else {
              fail(`go2rtc: ${val}`);
            }
          }
        }
        return;
      }

      // Mensaje binario = chunk fMP4
      const chunk = m.data instanceof ArrayBuffer
        ? (m.data as ArrayBuffer)
        : await (m.data as Blob).arrayBuffer();
      queue.push(chunk);
      flush();
    };

    ws.onerror = () => {
      if (cancelled) return;
      if (!playingRef.current) fail("Conexion WebSocket fallo. Verifica nginx y go2rtc.");
    };

    ws.onclose = (ev) => {
      if (cancelled) return;
      if (stoppedRef.current || playingRef.current) return;
      // Si el handler de error ya programo un retry, NO mostramos fail aqui
      // (el setTimeout va a reabrir el WS con una conexion nueva).
      if (retryTimer !== null) return;
      // Close abrupto (sin frame close ni mensaje {type:"error"} previo) suele
      // significar que go2rtc detecto un read timeout o reset del RTSP del NVR
      // y cerro el WS por su lado. Reintentar 1 vez con el mismo patron que el
      // JSON error transitorio cubre el caso "el NVR estaba puntualmente
      // saturado". Si el segundo intento tambien falla, mostramos fail con
      // mensaje accionable.
      if (
        ABNORMAL_WS_CLOSE_CODES.has(ev.code)
        && !retryAttemptedRef.current
      ) {
        console.info("[player] ws close abrupt code=%s, retrying...", ev.code);
        retryAttemptedRef.current = true;
        if (connectTimer) { clearTimeout(connectTimer); connectTimer = null; }
        retryTimer = setTimeout(() => {
          if (cancelled || stoppedRef.current) return;
          setInternalRetry((n) => n + 1);
        }, RETRY_DELAY_MS);
        return;
      }
      fail(`El NVR cerro la conexion sin entregar datos. Posible saturacion del puerto RTSP o lentitud temporaria. (code=${ev.code})`);
    };

    return () => {
      cancelled = true;
      stoppedRef.current = true;
      if (connectTimer) clearTimeout(connectTimer);
      if (retryTimer) clearTimeout(retryTimer);
      video.removeEventListener("playing", onPlaying);

      try { ws.close(); } catch {}

      const sb = sourceBufferRef.current;
      if (sb) {
        try { sb.removeEventListener("updateend", flush); } catch {}
        try { if (sb.updating) sb.abort(); } catch {}
      }
      if (mediaSource.readyState === "open") {
        try { mediaSource.endOfStream(); } catch {}
      }
      try { video.pause(); } catch {}
      try { video.removeAttribute("src"); video.load(); } catch {}

      if (objectUrlRef.current) {
        try { URL.revokeObjectURL(objectUrlRef.current); } catch {}
        objectUrlRef.current = null;
      }

      mediaSourceRef.current = null;
      sourceBufferRef.current = null;
      wsRef.current = null;
      setState("stopped");
    };
  }, [streamName, opts.audio, retryNonce, internalRetry]);

  return { videoRef, state, error };
}
